import React, { useState, useEffect } from "react";
import { FaEdit, FaGripVertical, FaPlus, FaTrash } from "react-icons/fa";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import "./css/OBPopup.css";
import { BACKEND_URL } from "../constants";
import { useAuth } from "../navigation/AuthProvider";
import axios from "axios";

// Hjälpfunktion: sortera stigande på priority (1 överst)
const sortByPriorityAsc = (arr = []) =>
  [...arr].sort((a, b) => (a?.priority ?? Number.MAX_SAFE_INTEGER) - (b?.priority ?? Number.MAX_SAFE_INTEGER));

export const OBPopup = ({ onClose, onSetUpdateShifts, readOnly}) => {
  const { user, company, obs, setObs, draftSchedule, setDraftSchedule, obUpdateInfo, setObUpdateInfo } = useAuth();
  const isReadOnly = readOnly === true;

  const [obRules, setObRules] = useState(sortByPriorityAsc(obs || []));
  const [selectedOB, setSelectedOB] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [saveClickable, setSaveClickable] = useState(false)

  const [obOverlaps, setObOverlaps] = useState(company?.obOverlaps ?? true);
  const [loading, setLoading] = useState(false)

  const [showResumeConfirmation, setShowResumeConfirmation] = useState(false);
  const [hasStartedBatching, setHasStartedBatching] = useState(false);

  // Håll lokala regler i sync med obs från context, sorterat på priority
  useEffect(() => {
    setObRules(sortByPriorityAsc(obs || []));
  }, [obs]);

  console.log(obUpdateInfo)

  useEffect(() => {
    if (
      obUpdateInfo.isUpdating &&
      obUpdateInfo.status !== "done" &&
      obUpdateInfo.mode === "resumed" &&
      !hasStartedBatching &&
      user &&
      company
    ) {
      setShowResumeConfirmation(true);
    }
  }, [obUpdateInfo, hasStartedBatching, user, company]);

  const startBatchUpdate = async () => {
    if (readOnly) return;
    let totalProcessed = 0;

    if (obUpdateInfo.total === 0 && company && user) {
      try {
        const { data: totalData } = await axios.get(`${BACKEND_URL}/get-total-ob-shifts`, {
          params: { companyId: company._id, userId: user._id }
        });
        if (totalData.status === "success") {
          setObUpdateInfo(prev => ({
            ...prev,
            total: totalData.totalShifts
          }));
        }
      } catch (err) {
        console.error("Kunde inte hämta totalShifts:", err);
      }
    }
  
    try {
      while (true) {
        const { data } = await axios.post(`${BACKEND_URL}/run-ob-update-batch`, {
          companyId: company._id,
          userId: user._id,
        });
  
        if (data.status !== "success") {
          setObUpdateInfo((prev) => ({
            ...prev,
            progress: totalProcessed,
            status: "error",
          }));
          alert("OB batch update misslyckades: " + (data.errorText || "Okänt fel"));
          break;
        }
  
        totalProcessed += data.processed;
  
        const newStatus = obUpdateInfo.status === "preparing" ? "running" : obUpdateInfo.status;
        setObUpdateInfo((prev) => ({
          ...prev,
          progress: totalProcessed,
          status: newStatus,
          isUpdating: true,
        }));
  
        if (data.done) {
          setObUpdateInfo({
            progress: totalProcessed,
            total: data.totalShifts,
            status: "done",
            isUpdating: false,
            companyId: company._id,
            startedAt: obUpdateInfo.startedAt,
          });
          alert(`✅ OB-uppdatering klar. Totalt uppdaterade skift: ${totalProcessed}`);
          window.location.reload();
          break;
        }
  
        await new Promise((r) => setTimeout(r, 100));
      }
    } catch (err) {
      console.error("❌ OB update batch error:", err);
      setObUpdateInfo((prev) => ({
        ...prev,
        progress: totalProcessed,
        status: "error",
      }));
      alert("Ett fel uppstod under OB-uppdateringen.");
    }
  };

  const handleSaveAllChanges = async () => {
    if (!user || !company) return;
    if (readOnly) return;
    setObUpdateInfo({
      companyId: company._id,
      startedAt: Date.now(),
      status: "preparing",
      isUpdating: true // ✅ Sätt detta direkt
    });
    

    try {
      const payload = {
        userId: user._id,
        companyId: company._id,
        obRules: obRules.map((rule, i) => ({
          ...rule,
          priority: i + 1,
        })),
        obOverlaps,
      };

      const { data: syncData } = await axios.put(`${BACKEND_URL}/sync-ob-rules`, payload);

      if (syncData.status !== "success") {
        alert(syncData.errorText || "Kunde inte spara OB-regler.");
        return;
      }

      setObs(syncData.obRules);
      setDraftSchedule(syncData.shifts);
      setSaveClickable(false);
      setObUpdateInfo({progress: 0, total: syncData.totalShifts, companyId: company._id, startedAt: Date.now(), status: "preparing", isUpdating: true})
      await startBatchUpdate()
    } catch (err) {
      console.error("❌ Fel vid sparande av OB-regler:", err);
      alert("Kunde inte spara OB-regler – försök igen.");
      return;
    }

    
  };

  const handleToggleOverlap = () => {
    if (readOnly) return;
    const newValue = !obOverlaps;
  
    const confirmed = window.confirm(
      "⚠️ Är du säker på att du vill ändra OB-överlappningsinställningen?\n\n" +
      (newValue
        ? "OB-regler kommer att kunna kombineras om tider överlappar."
        : "Endast regeln med högst prioritet kommer att användas vid överlapp.")
    );
  
    if (!confirmed) return;
  
    // ✅ Uppdatera lokalt — ingen backend request här
    setObOverlaps(newValue);
  
    // 💡 Eventuellt: visa en indikator att något har ändrats och behöver sparas
    setSaveClickable(true)
  };

  const handleSaveOBRule = (newRule) => {
    if (readOnly) return;
    setLoading(true);
  
    if (selectedOB) {
      // Update existing rule i lokal state och håll listan sorterad på priority
      setObRules((prev) => {
        const next = (Array.isArray(prev)
          ? prev.map((rule) =>
              rule._id === selectedOB._id ? { ...selectedOB, ...newRule } : rule
            )
          : []);
        return sortByPriorityAsc(next);
      });
    } else {
      // Create new rule med nästa priority och håll listan sorterad
      const newPriority = obRules.length ? obRules.length + 1 : 1;
      const tempId = Date.now().toString();
      const newOb = {
        ...newRule,
        _id: tempId,
        priority: newPriority,
      };
      setObRules((prev) => sortByPriorityAsc([...(prev || []), newOb]));
    }
  
    setSelectedOB(null);
    setSaveClickable(true)
    setLoading(false);
  };

  const handleDeleteOBRule = async (id) => {
    if (readOnly) return;
    setObRules((prev) => prev.filter((rule) => rule._id !== id));
    setSaveClickable(true)
  };

  const OBRuleItem = ({ rule, index }) => {
    const [{ isDragging }, drag] = useDrag({
      type: "OB_RULE",
      item: { index },
      canDrag: !obOverlaps && !readOnly,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });
    
    const [{ isOver }, drop] = useDrop({
      accept: "OB_RULE",
      hover: () => {},
      drop: async (draggedItem) => {
      
        const updatedRules = [...obRules];
        const [movedItem] = updatedRules.splice(draggedItem.index, 1);
        updatedRules.splice(index, 0, movedItem);
      
        const reorderedRules = updatedRules.map((rule, i) => ({
          ...rule,
          priority: i + 1,
        }));
        try {
          setObRules(reorderedRules);
          setSaveClickable(true)
        } catch (error) {
          console.error("Fel vid uppdatering av prioritet:", error);
          alert("Ett fel inträffade vid uppdatering av prioritet");
        }
      },
      
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });
    const [isExpanded, setIsExpanded] = useState(false);
  
    return (
      <div
        ref={(node) => {
          if (!obOverlaps) drag(drop(node));
        }}
        className={`ob-rule-item ${isDragging ? "ob-dragging" : ""} ${obOverlaps ? "disabled-drag" : ""}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {obOverlaps === false && (
          <FaGripVertical className="ob-drag-icon" />
        )}
          
        <div className="ob-rule-content">
          <div className="ob-rule-header">
            <div className="ob-rule-name">{rule.name}</div>
            {obOverlaps === false && (
              <div className="ob-rule-priority">Prioritet {rule.priority || index + 1}</div>
            )}
          </div>
          
          <div className={`ob-rule-details ${isExpanded ? 'expanded' : ''}`}>
            <span><strong>Tid:</strong> {rule.startTime} - {rule.endTime}</span>
            <span>
              <strong>Tillägg:</strong>{" "}
              {rule.type === "percentage"
                ? `${rule.value}%`
                : `${rule.value ?? rule.additionalPay} kr/h`}
            </span>
            <span><strong>Dagar:</strong> {rule.daysApplied?.join(", ") || "-"}</span>
            <span><strong>Högtider:</strong> {rule.specialHolidayNames?.join(", ") || "-"}</span>
          </div>
        </div>
        <div className="ob-rule-actions">
          {!readOnly && (
            <FaEdit
              className="ob-edit-icon"
              onClick={(e) => {
                e.stopPropagation();
                editOBRule(rule);
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const editOBRule = (rule) => {
    if (readOnly) return;
    setSelectedOB(rule);
    setShowAddForm(true);
  };

  const OBProgressOverlay = () => {
    const { obUpdateInfo } = useAuth(); // 👈 Ta från context
  
    const percentage =
      obUpdateInfo.total > 0
        ? Math.round((obUpdateInfo.progress / obUpdateInfo.total) * 100)
        : 0;
  
    const isPreparing = obUpdateInfo.status === "preparing";
  
    return (
      <div className="ob-progress-overlay">
        <div className="ob-progress-popup">
          <h2 className="ob-progress-title">
            {isPreparing ? "Förbereder OB-data..." : "Uppdaterar OB-data"}
          </h2>
  
          <div className="ob-progress-bar-wrapper">
            <div className="ob-progress-bar">
              <div
                className="ob-progress-bar-fill"
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
            <span className="ob-progress-percent">{percentage}%</span>
          </div>
  
          <p className="ob-progress-status-text">
            {isPreparing
              ? "Hämtar skiftinformation..."
              : `${obUpdateInfo.progress} av ${obUpdateInfo.total} skift bearbetade...`}
          </p>
        </div>
      </div>
    );
  };
  
  return (
    <div className="ob-popup-overlay" onClick={onClose}>
      <div className="ob-popup-container" onClick={(e) => e.stopPropagation()}>
        <h2 className="ob-popup-title">OB-Regler</h2>
        <p className="ob-hierarchy-info">
          Regler listade högre upp har högre prioritet och åsidosätter lägre regler vid överlapp.
        </p>

        <DndProvider backend={HTML5Backend}>
          <div className="ob-rules-list">
            {obRules.map((rule, index) => (
              <OBRuleItem key={rule._id} rule={rule} index={index} />
            ))}
          </div>
        </DndProvider>

        <div className="ob-overlap-setting">
          <p className="ob-overlap-title">
            Ska OB-regler vara <strong>överlappande</strong>?
          </p>
          <p className="ob-overlap-description">
            <strong>Aktiverat:</strong> Flera OB-regler kan gälla samtidigt vid överlappande tider (t.ex. OB-A + OB-B).<br />
            <strong>Inaktiverat:</strong> Endast OB-regeln med högst prioritet används – andra ignoreras vid överlapp.
          </p>

          <div className="ob-overlap-toggle">
            <label className="switch">
              <input
                type="checkbox"
                checked={obOverlaps}
                onChange={handleToggleOverlap}
              />
              <span className="slider"></span>
            </label>
            <span className="status-label">
              {obOverlaps ? "Överlappning aktiverad" : "Överlappning inaktiverad"}
            </span>
          </div>
        </div>

        <div className="ob-button-row">
          {!readOnly && (
            <button className="ob-add-button" onClick={() => setShowAddForm(true)}>
              <FaPlus /> Lägg till ny OB
            </button>
          )}

          <button
            className="ob-save-button"
            disabled={readOnly || !saveClickable}
            onClick={handleSaveAllChanges}
          >
            Spara ändringar
          </button>

        </div>

        {showAddForm && (
          <OBSettingsForm
            obRule={selectedOB}
            onClose={() => {
              setShowAddForm(false);
              setSelectedOB(null);
            }}
            onSave={handleSaveOBRule}
            onDelete={handleDeleteOBRule}
          />
        )}
      </div>
      {obUpdateInfo.status !== "done" && obUpdateInfo.isUpdating && (
        <div className="ob-update-lock">
          <OBProgressOverlay />
        </div>
      )}

      {showResumeConfirmation && (
        <div className="ob-resume-confirm-overlay">
          <div className="ob-resume-confirm-popup">
            <h3>OB-uppdatering avbröts</h3>
            <p>En tidigare OB-uppdatering avbröts. Klicka på “Fortsätt” för att återuppta bearbetningen.</p>
            <button
              className="ob-resume-button"
              onClick={() => {
                setObUpdateInfo((prev) => ({
                  ...prev,
                  mode: "manual", // eller t.ex. "resumed_started"
                }));
                startBatchUpdate();
                setHasStartedBatching(true);
                setShowResumeConfirmation(false);
              }}
            >
              Fortsätt
            </button>

          </div>
        </div>
      )}

    </div>
  );
};

const OBSettingsForm = ({ obRule, onClose, onSave, onDelete, obRules }) => {
  const [obName, setObName] = useState(obRule ? obRule.name : "");
  const [obStartTime, setObStartTime] = useState(obRule ? obRule.startTime : "");
  const [obEndTime, setObEndTime] = useState(obRule ? obRule.endTime : "");

  const [obDays, setObDays] = useState(obRule && !obRule.specialDatesSelected ? obRule.daysApplied : []);
  const [obSpecialDates, setObSpecialDates] = useState(obRule && obRule.specialDatesSelected ? obRule.specialHolidayNames : []);
  
  const [type, setType] = useState(obRule?.type || "fixed");
  const [value, setValue] = useState(
    obRule?.value ?? obRule?.additionalPay ?? ""
  );

  const [selectedDate, setSelectedDate] = useState(null);
  const [useSpecialDates, setUseSpecialDates] = useState(obRule ? obRule.specialDatesSelected : false);
  console.log("use special dates ", useSpecialDates)

  const daysOptions = [
    { value: "Måndag", label: "Måndag" },
    { value: "Tisdag", label: "Tisdag" },
    { value: "Onsdag", label: "Onsdag" },
    { value: "Torsdag", label: "Torsdag" },
    { value: "Fredag", label: "Fredag" },
    { value: "Lördag", label: "Lördag" },
    { value: "Söndag", label: "Söndag" },
  ];

  const holidayOptions = [
    { value: "Nyårsdagen", label: "Nyårsdagen" },
    { value: "Trettondedag jul", label: "Trettondedag jul" },
    { value: "Skärtorsdagen", label: "Skärtorsdagen" },
    { value: "Långfredagen", label: "Långfredagen" },
    { value: "Påskafton", label: "Påskafton" },
    { value: "Påskdagen", label: "Påskdagen" },
    { value: "Annandag påsk", label: "Annandag påsk" },
    { value: "Valborgsmässoafton", label: "Valborgsmässoafton" },
    { value: "Kristi himmelsfärdsdag", label: "Kristi himmelsfärdsdag" },
    { value: "Pingstafton", label: "Pingstafton" },
    { value: "Pingstdagen", label: "Pingstdagen" },
    { value: "Nationaldagen", label: "Nationaldagen" },
    { value: "Midsommarafton", label: "Midsommarafton" },
    { value: "Midsommardagen", label: "Midsommardagen" },
    { value: "Alla helgons afton", label: "Alla helgons afton" },
    { value: "Alla helgons dag", label: "Alla helgons dag" },
    { value: "Lucia", label: "Lucia" },
    { value: "Julafton", label: "Julafton" },
    { value: "Juldagen", label: "Juldagen" },
    { value: "Annandag jul", label: "Annandag jul" },
    { value: "Nyårsafton", label: "Nyårsafton" }
  ];
  

  const handleSave = () => {
    const newRule = {
      name: obName,
      startTime: obStartTime,
      endTime: obEndTime,
      daysApplied: useSpecialDates ? [] : obDays,
      specialHolidayNames: useSpecialDates ? obSpecialDates : [],
      type,
      value: Number(value),

      // 🔥 VIKTIG fallback
      additionalPay: type === "fixed" ? Number(value) : 0,

      priority: obRule ? obRule.priority : null,
      specialDatesSelected: useSpecialDates
    };
    onSave(newRule);
    onClose();
  };

  const handleToggleSpecialDates = () => {
    setUseSpecialDates((prev) => {
      const newValue = !prev;
      if (newValue) {
        setObDays([]);
      } else {
        setObSpecialDates([]);
      }
      return newValue;
    });
  };

  return (
    <div className="ob-popup-form-overlay">
      <div className="ob-popup-form">
        <h3 className="ob-settings-title">{obRule ? "Redigera OB-regel" : "Lägg till ny OB-regel"}</h3>

        <div className="form-row">
          <div className="form-group">
            <label>Namn</label>
            <input
              type="text"
              placeholder="till exempel OB-A..."
              value={obName}
              onChange={(e) => setObName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>OB-tillägg</label>

            <div style={{ display: "flex", gap: "10px" }}>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="fixed">kr/h</option>
                <option value="percentage">%</option>
              </select>

              <input
                type="number"
                placeholder={type === "fixed" ? "t.ex. 50" : "t.ex. 100"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Starttid</label>
            <input
              type="time"
              value={obStartTime}
              onChange={(e) => setObStartTime(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Sluttid</label>
            <input
              type="time"
              value={obEndTime}
              onChange={(e) => setObEndTime(e.target.value)}
            />
          </div>
        </div>

        <div className="form-group toggle-checkbox">
          <input
            type="checkbox"
            id="useSpecialDates"
            checked={useSpecialDates}
            onChange={handleToggleSpecialDates}
          />
          <label htmlFor="useSpecialDates" className="toggle-label">
            <span className="toggle-text">Använd specifika datum istället för veckodagar</span>
          </label>
        </div>

        {!useSpecialDates ? (
          <div className="form-group">
            <label>Dagar</label>
            <Select
              isMulti
              options={daysOptions}
              value={daysOptions.filter((option) => obDays.includes(option.value))}
              onChange={(selectedOptions) =>
                setObDays(selectedOptions.map((option) => option.value))
              }
            />
          </div>
        ) : (
          <div className="form-group">
            <label>Välj högtider</label>
            <Select
              isMulti
              options={holidayOptions}
              value={holidayOptions.filter((opt) =>
                obSpecialDates.includes(opt.value)
              )}
              onChange={(selectedOptions) => {
                const selectedNames = selectedOptions.map((opt) => opt.value);
                setObSpecialDates(selectedNames);
              }}
            />
          </div>
        )}

        <div className="ob-popup-buttons">
          <button className="ob-save-button" onClick={handleSave}>
            Spara
          </button>
          {obRule && (
            <button
              className="ob-delete-button"
              onClick={() => {
                onDelete(obRule._id);
                onClose();
              }}
            >
              <FaTrash /> Radera
            </button>
          )}
          <button className="ob-cancel-button" onClick={onClose}>
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
};
